import React from 'react'
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  keyframes
} from '@mui/material'
import AudioFileIcon from '@mui/icons-material/AudioFile'
import VideoFileIcon from '@mui/icons-material/VideoFile'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import VisibilityIcon from '@mui/icons-material/Visibility'
import DeleteIcon from '@mui/icons-material/Delete'
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove'
import EditIcon from '@mui/icons-material/Edit'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import ErrorIcon from '@mui/icons-material/Error'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import StorageIcon from '@mui/icons-material/Storage'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import { FileItem } from '../../../services/api'

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

// Animação de ondas de som (transcrição em andamento)
const soundWave = keyframes`
  0%, 100% { transform: scaleY(0.3); }
  50% { transform: scaleY(1); }
`

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(0.95); }
`

// Formatação do tempo estimado
function formatEstimatedTime(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return ''
  
  if (seconds < 60) {
    return `~${seconds}s restantes`
  }
  
  const minutes = Math.ceil(seconds / 60)
  if (minutes === 1) {
    return '~1 min restante'
  }
  
  return `~${minutes} min restantes`
}

// Descrição do estado de processamento
function getProcessingStateDescription(state: string | null | undefined): string {
  switch (state) {
    case 'PENDING':
      return 'Aguardando...'
    case 'UPLOADING':
      return 'Enviando arquivo...'
    case 'TRANSCRIBING':
      return 'Transcrevendo áudio...'
    case 'PROCESSING_TRANSCRIPTION':
      return 'Processando transcrição...'
    case 'ANALYZING':
      return 'Analisando com IA...'
    case 'COMPLETED':
      return 'Concluído'
    case 'FAILED':
      return 'Falhou'
    case 'WARNING':
      return 'Não elegível'
    default:
      return 'Processando...'
  }
}

type ViewMode = 'grid' | 'list'

type Props = {
  file: FileItem
  viewMode: ViewMode
  onOpen: (file: FileItem) => void
  onDelete: (fileId: string) => void
  // Selection props
  selectionMode?: boolean
  selected?: boolean
  onSelect?: (file: FileItem) => void
  /** IDs de todos os arquivos selecionados (para drag de múltiplos) */
  selectedFileIds?: string[]
  /** Habilita arrastar o card para mover entre pastas */
  draggable?: boolean
  /** Mover arquivo para pasta */
  onMoveToFolder?: (file: FileItem) => void
  /** Renomear arquivo (displayName) */
  onRename?: (file: FileItem) => void
}

export default function FileItemCard({
  file,
  viewMode,
  onOpen,
  onDelete,
  selectionMode = false,
  selected = false,
  onSelect,
  selectedFileIds = [],
  draggable: isDraggable = false,
  onMoveToFolder,
  onRename
}: Props) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const menuOpen = Boolean(anchorEl)

  const isCompleted = file.status === 'completed'
  const isProcessing = file.status === 'processing'
  const isWarning = file.processingState === 'WARNING'
  const isFailed = file.status === 'failed' && !isWarning
  const progress = typeof file.progress === 'number' ? file.progress : isCompleted ? 100 : 50

  // Nome de exibição: displayName personalizado tem prioridade sobre o title original
  const fileDisplayName = file.displayName || file.title

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setAnchorEl(e.currentTarget)
  }

  const handleMenuClose = () => setAnchorEl(null)

  const handleAction = (action: () => void) => {
    handleMenuClose()
    action()
  }

  // ── Drag support para mover entre pastas ──
  const handleDragStart = (e: React.DragEvent) => {
    if (!isDraggable) return
    
    // Se este arquivo está entre os selecionados, arrastar todos
    // Caso contrário, arrastar apenas este arquivo
    const dragIds = selectedFileIds.includes(file.fileId) && selectedFileIds.length > 0
      ? selectedFileIds
      : [file.fileId]
    
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'file',
      id: file.fileId,
      ids: dragIds,
      name: fileDisplayName
    }))
    e.dataTransfer.effectAllowed = 'move'
  }

  // Detecta tipo do arquivo (reutilizado em FileIcon e CompletedFilePreview)
  const fileTypeInfo = (() => {
    const type = file.type?.toLowerCase() || ''
    const titleLower = file.title?.toLowerCase() || ''
    const audioTypes = ['audio', 'mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a', 'wma']
    const videoTypes = ['video', 'mp4', 'avi', 'mov', 'mkv', 'webm', 'wmv', 'flv', 'm4v']
    const isAudio = audioTypes.some(t => type.includes(t) || titleLower.endsWith(`.${t}`))
    const isVideo = videoTypes.some(t => type.includes(t) || titleLower.endsWith(`.${t}`))
    const extMatch = file.title?.match(/\.([a-zA-Z0-9]+)$/)
    const ext = extMatch ? extMatch[1].toUpperCase() : (file.type?.toUpperCase().split('/').pop() || 'FILE')
    return { isAudio, isVideo, ext }
  })()

  const FileIcon = () => {
    const { isAudio, isVideo } = fileTypeInfo
    const iconSx = { fontSize: viewMode === 'grid' ? 48 : 32 }
    if (isAudio) return <AudioFileIcon sx={{ ...iconSx, color: '#1976d2' }} />
    if (isVideo) return <VideoFileIcon sx={{ ...iconSx, color: '#9c27b0' }} />
    return <InsertDriveFileIcon sx={{ ...iconSx, color: '#757575' }} />
  }

  // Preview visual para arquivos concluídos na view grid
  const CompletedFilePreview = () => {
    const { isAudio, isVideo, ext } = fileTypeInfo

    const ExtBadge = ({ color }: { color: string }) => (
      <Chip
        label={ext}
        size="small"
        sx={{
          height: 20,
          fontSize: '0.65rem',
          fontWeight: 700,
          letterSpacing: '0.06em',
          color,
          border: `1px solid ${color}55`,
          bgcolor: `${color}11`,
          '& .MuiChip-label': { px: 0.75 }
        }}
      />
    )

    if (isAudio) {
      const waveHeights = [0.35, 0.6, 0.9, 1.0, 0.75, 0.55, 0.85, 0.65, 0.4]
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25, width: '100%', px: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', height: 44 }}>
            {waveHeights.map((h, i) => (
              <Box
                key={i}
                sx={{
                  width: 5,
                  height: `${Math.round(h * 44)}px`,
                  bgcolor: '#4caf50',
                  borderRadius: '2px',
                  opacity: 0.65,
                }}
              />
            ))}
          </Box>
          <ExtBadge color="#1976d2" />
        </Box>
      )
    }

    if (isVideo) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{
            width: 46, height: 46,
            borderRadius: '50%',
            border: '2.5px solid #9c27b0',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            opacity: 0.75,
          }}>
            {/* Triângulo play */}
            <Box sx={{
              width: 0, height: 0,
              borderTop: '9px solid transparent',
              borderBottom: '9px solid transparent',
              borderLeft: '16px solid #9c27b0',
              ml: '3px',
            }} />
          </Box>
          <ExtBadge color="#9c27b0" />
        </Box>
      )
    }

    // Documento genérico: linhas de texto
    const lineWidths = [1, 0.72, 0.88, 0.55]
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', width: 52 }}>
          {lineWidths.map((w, i) => (
            <Box
              key={i}
              sx={{
                height: 5,
                width: `${Math.round(w * 52)}px`,
                bgcolor: '#78909c',
                borderRadius: '2px',
                opacity: 0.6,
              }}
            />
          ))}
        </Box>
        <ExtBadge color="#546e7a" />
      </Box>
    )
  }

  const StatusBadge = () => {
    if (isCompleted) {
      return (
        <Tooltip title="Transcrição concluída">
          <CheckCircleIcon sx={{ fontSize: 18, color: '#4caf50' }} />
        </Tooltip>
      )
    }
    if (isWarning) {
      return (
        <Tooltip title="Arquivo não elegível para transcrição">
          <ReportProblemIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
        </Tooltip>
      )
    }
    if (isFailed) {
      return (
        <Tooltip title="Erro no processamento">
          <ErrorIcon sx={{ fontSize: 18, color: '#f44336' }} />
        </Tooltip>
      )
    }
    // Animação de ondas de som para transcrição em andamento
    return (
      <Tooltip title={file.message || 'Transcrevendo...'}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '2px',
            height: 18,
            animation: `${pulse} 2s ease-in-out infinite`
          }}
        >
          {[0, 1, 2, 3, 4].map((i) => (
            <Box
              key={i}
              sx={{
                width: 3,
                height: '100%',
                bgcolor: '#ff9800',
                borderRadius: '1px',
                animation: `${soundWave} 0.8s ease-in-out infinite`,
                animationDelay: `${i * 0.1}s`,
                transformOrigin: 'bottom'
              }}
            />
          ))}
        </Box>
      </Tooltip>
    )
  }

  const formatBytes = (bytes: number) => {
    if (!bytes) return '—'
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1)
    const value = bytes / 1024 ** i
    return `${value.toFixed(value >= 10 ? 0 : 1)} ${sizes[i]}`
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—'
    // O backend retorna timestamp como string numérica (ex: "1769454102923")
    const timestamp = /^\d+$/.test(dateStr) ? parseInt(dateStr, 10) : Date.parse(dateStr)
    if (isNaN(timestamp)) return '—'
    
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) {
      return `Hoje, ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
    }
    if (diffDays === 1) {
      return `Ontem, ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
    }
    if (diffDays < 7) {
      return date.toLocaleDateString('pt-BR', { weekday: 'short', hour: '2-digit', minute: '2-digit' })
    }
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
  }

  // View em Grid (similar ao Drive)
  if (viewMode === 'grid') {
    const handleClick = () => {
      // Clique no card sempre abre análise/status
      onOpen(file)
    }

    return (
      <Box
        onClick={handleClick}
        draggable={isDraggable}
        onDragStart={handleDragStart}
        sx={{
          bgcolor: selected 
            ? 'rgba(25, 118, 210, 0.04)' 
            : isCompleted 
              ? '#fff' 
              : '#fafafa',
          borderRadius: '0 0 12px 12px',
          border: selected 
            ? '2px solid #1976d2' 
            : isCompleted 
              ? '1px solid #c8e6c9'
              : isFailed
                ? '1px solid #ffcdd2'
                : '1px solid #e0e0e0',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          position: 'relative',
          boxShadow: isCompleted 
            ? '0 2px 8px rgba(76, 175, 80, 0.1)' 
            : 'none',
          '&:hover': {
            borderColor: isCompleted ? '#4caf50' : '#1976d2',
            boxShadow: isCompleted 
              ? '0 6px 20px rgba(76, 175, 80, 0.2)' 
              : '0 4px 12px rgba(25, 118, 210, 0.15)',
            transform: isCompleted ? 'translateY(-3px)' : 'translateY(-1px)',
            '& .file-actions': { opacity: 1 },
            '& .selection-checkbox': { opacity: 1 }
          }
        }}
      >
        {/* Selection checkbox - sempre visível no hover */}
        <Checkbox
          checked={selected}
          className="selection-checkbox"
          sx={{
            position: 'absolute',
            top: 4,
            left: 4,
            zIndex: 10,
            bgcolor: 'white',
            borderRadius: 1,
            opacity: selected ? 1 : 0,
            transition: 'opacity 0.2s',
            '&:hover': { bgcolor: 'white' },
            '& .MuiSvgIcon-root': { fontSize: 20 }
          }}
          onClick={(e) => {
            e.stopPropagation()
            if (onSelect) onSelect(file)
          }}
        />

        {/* Área do ícone */}
        <Box
          sx={{
            height: 120,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: selected 
              ? 'rgba(25, 118, 210, 0.08)' 
              : isCompleted 
                ? 'linear-gradient(180deg, #e8f5e9 0%, #f5f5f5 100%)'
                : isFailed
                  ? '#fff5f5'
                  : '#f8f9fa',
            background: selected 
              ? 'rgba(25, 118, 210, 0.08)' 
              : isCompleted 
                ? 'linear-gradient(180deg, #e8f5e9 0%, #f5f5f5 100%)'
                : isFailed
                  ? '#fff5f5'
                  : '#f8f9fa',
            position: 'relative',
            borderBottom: isCompleted ? '2px solid #c8e6c9' : '1px solid #f0f0f0',
          }}
        >
          {isCompleted ? <CompletedFilePreview /> : <FileIcon />}

          {/* Status badge */}
          <Box sx={{ position: 'absolute', top: 8, left: 8 }}>
            <StatusBadge />
          </Box>

          {/* Menu button */}
          <Box
            className="file-actions"
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              opacity: menuOpen ? 1 : 0,
              transition: 'opacity 0.2s'
            }}
          >
            <IconButton size="small" onClick={handleMenuOpen}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>


        </Box>

        {/* Info do arquivo */}
        <Box sx={{ p: 1.5 }}>
          {/* Nome do arquivo */}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              mb: 0.75,
              color: '#333'
            }}
            title={fileDisplayName}
          >
            {fileDisplayName}
          </Typography>

          {/* Status chip — altura fixa para manter todos os cards com o mesmo tamanho vertical */}
          <Box sx={{ mb: 1, minHeight: 56, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {isCompleted && (
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                label="Concluído"
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  bgcolor: '#e8f5e9',
                  color: '#2e7d32',
                  '& .MuiChip-icon': { color: '#2e7d32' }
                }}
              />
            )}
            {isFailed && (
              <Chip
                icon={<ErrorIcon sx={{ fontSize: 14 }} />}
                label="Erro"
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  bgcolor: '#ffebee',
                  color: '#c62828',
                  '& .MuiChip-icon': { color: '#c62828' }
                }}
              />
            )}
            {isWarning && (
              <Chip
                icon={<ReportProblemIcon sx={{ fontSize: 14 }} />}
                label="Não elegível"
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  bgcolor: '#fff8e1',
                  color: '#e65100',
                  '& .MuiChip-icon': { color: '#f59e0b' }
                }}
              />
            )}
            {isProcessing && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {/* Linha: ícone + label + % */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <HourglassEmptyIcon sx={{ fontSize: 13, color: '#e65100', animation: `${spin} 2s linear infinite` }} />
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#e65100', fontWeight: 600 }}>
                      {getProcessingStateDescription(file.processingState)}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#999', fontWeight: 500 }}>
                    {progress}%
                  </Typography>
                </Box>

                {/* Barra de progresso */}
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 5,
                    borderRadius: 3,
                    bgcolor: 'rgba(255, 152, 0, 0.15)',
                    '& .MuiLinearProgress-bar': { bgcolor: '#ff9800', borderRadius: 3 }
                  }}
                />

                {/* Tempo estimado */}
                {file.estimatedSecondsRemaining != null && file.estimatedSecondsRemaining > 0 && (
                  <Typography variant="caption" sx={{ color: '#bbb', fontSize: '0.63rem' }}>
                    {formatEstimatedTime(file.estimatedSecondsRemaining)}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* Metadados: Data e Tamanho */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarTodayIcon sx={{ fontSize: 12, color: '#999' }} />
              <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem' }}>
                {formatDate(file.createdAt)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <StorageIcon sx={{ fontSize: 12, color: '#999' }} />
              <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem' }}>
                {formatBytes(file.bytes)} • {file.type?.toUpperCase() || 'ARQUIVO'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Menu de ações */}
        <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose} onClick={(e) => e.stopPropagation()}>
          {/* Opção de seleção */}
          {onSelect && (
            <MenuItem
              onClick={(e) => {
                e.stopPropagation()
                handleAction(() => onSelect(file))
              }}
              sx={{ color: selected ? 'primary.main' : 'inherit' }}
            >
              <ListItemIcon>
                {selected ? (
                  <CheckBoxIcon fontSize="small" color="primary" />
                ) : (
                  <CheckBoxOutlineBlankIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText>{selected ? 'Desmarcar' : 'Selecionar'}</ListItemText>
            </MenuItem>
          )}
          <MenuItem
            onClick={(e) => {
              e.stopPropagation()
              handleAction(() => onOpen(file))
            }}
          >
            <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Visualizar</ListItemText>
          </MenuItem>
          {onMoveToFolder && (
            <MenuItem onClick={(e) => {
              e.stopPropagation()
              handleAction(() => onMoveToFolder(file))
            }}>
              <ListItemIcon><DriveFileMoveIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Mover para pasta</ListItemText>
            </MenuItem>
          )}
          {onRename && (
            <MenuItem onClick={(e) => {
              e.stopPropagation()
              handleAction(() => onRename(file))
            }}>
              <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Renomear</ListItemText>
            </MenuItem>
          )}
          <MenuItem
            onClick={(e) => {
              e.stopPropagation()
              handleAction(() => onDelete(file.fileId))
            }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Excluir</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    )
  }

  // View em Lista (similar ao Drive)
  const handleListClick = () => {
    // Clique no item sempre abre análise/status
    // Seleção é feita apenas via checkbox
    onOpen(file)
  }

  return (
    <Box
      onClick={handleListClick}
      draggable={isDraggable}
      onDragStart={handleDragStart}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, sm: 2 },
        px: { xs: 1.5, sm: 2 },
        py: { xs: 1, sm: 1.5 },
        borderBottom: '1px solid #f0f0f0',
        borderLeft: isCompleted ? '3px solid #4caf50' : isWarning ? '3px solid #f59e0b' : isFailed ? '3px solid #f44336' : '3px solid transparent',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        bgcolor: selected
          ? 'rgba(25, 118, 210, 0.04)'
          : isCompleted
            ? 'rgba(76, 175, 80, 0.02)'
            : 'transparent',
        '&:hover': {
          bgcolor: selected
            ? 'rgba(25, 118, 210, 0.08)'
            : isCompleted
              ? 'rgba(76, 175, 80, 0.06)'
              : '#f5f9ff',
          '& .file-actions': { opacity: 1 },
          '& .selection-checkbox': { opacity: 1 }
        },
        '&:last-child': { borderBottom: 'none' }
      }}
    >
      {/* Selection checkbox - visível no hover em desktop, sempre acessível em mobile */}
      <Checkbox
        checked={selected}
        className="selection-checkbox"
        sx={{
          display: { xs: 'none', sm: 'flex' },
          opacity: selected ? 1 : 0,
          transition: 'opacity 0.2s',
          '& .MuiSvgIcon-root': { fontSize: 20 }
        }}
        onClick={(e) => {
          e.stopPropagation()
          if (onSelect) onSelect(file)
        }}
      />

      {/* Ícone */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: { xs: 32, sm: 40 }, flexShrink: 0 }}>
        <FileIcon />
      </Box>

      {/* Nome e status */}
      <Box sx={{ flex: 1, minWidth: 0, minHeight: 64 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: '#333',
              fontSize: { xs: '0.85rem', sm: '0.875rem' },
              maxWidth: { xs: '100%', sm: 'none' }
            }}
            title={fileDisplayName}
          >
            {fileDisplayName}
          </Typography>

          {/* Status Chip inline - oculto em mobile, mostrado abaixo */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1 }}>
            {isCompleted && (
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}
                label="Concluído"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  bgcolor: '#e8f5e9',
                  color: '#2e7d32',
                  '& .MuiChip-icon': { color: '#2e7d32', ml: 0.5 },
                  '& .MuiChip-label': { px: 0.75 }
                }}
              />
            )}
            {isFailed && (
              <Chip
                icon={<ErrorIcon sx={{ fontSize: 12 }} />}
                label="Erro"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  bgcolor: '#ffebee',
                  color: '#c62828',
                  '& .MuiChip-icon': { color: '#c62828', ml: 0.5 },
                  '& .MuiChip-label': { px: 0.75 }
                }}
              />
            )}
            {isWarning && (
              <Chip
                icon={<ReportProblemIcon sx={{ fontSize: 12 }} />}
                label="Não elegível"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  bgcolor: '#fff8e1',
                  color: '#e65100',
                  '& .MuiChip-icon': { color: '#f59e0b', ml: 0.5 },
                  '& .MuiChip-label': { px: 0.75 }
                }}
              />
            )}
            {isProcessing && (
              <Chip
                icon={<HourglassEmptyIcon sx={{ fontSize: 12 }} />}
                label={getProcessingStateDescription(file.processingState)}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  bgcolor: '#fff3e0',
                  color: '#e65100',
                  '& .MuiChip-icon': { color: '#e65100', ml: 0.5 },
                  '& .MuiChip-label': { px: 0.75 }
                }}
              />
            )}
          </Box>
        </Box>

        {/* Info secundária: tipo + status (mobile) + tamanho (mobile) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ color: '#999', fontSize: '0.7rem' }}>
            {file.type?.toUpperCase() || 'ARQUIVO'}
          </Typography>

          {/* Tamanho - visível em mobile */}
          <Typography variant="caption" sx={{ color: '#999', fontSize: '0.7rem', display: { xs: 'block', md: 'none' } }}>
            • {formatBytes(file.bytes)}
          </Typography>

          {/* Status em mobile */}
          <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 0.5 }}>
            {isCompleted && (
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: 10 }} />}
                label="OK"
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  bgcolor: '#e8f5e9',
                  color: '#2e7d32',
                  '& .MuiChip-icon': { color: '#2e7d32', ml: 0.25 },
                  '& .MuiChip-label': { px: 0.5 }
                }}
              />
            )}
            {isFailed && (
              <Chip
                icon={<ErrorIcon sx={{ fontSize: 10 }} />}
                label="Erro"
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  bgcolor: '#ffebee',
                  color: '#c62828',
                  '& .MuiChip-icon': { color: '#c62828', ml: 0.25 },
                  '& .MuiChip-label': { px: 0.5 }
                }}
              />
            )}
            {isWarning && (
              <Chip
                icon={<ReportProblemIcon sx={{ fontSize: 10 }} />}
                label="Aviso"
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  bgcolor: '#fff8e1',
                  color: '#e65100',
                  '& .MuiChip-icon': { color: '#f59e0b', ml: 0.25 },
                  '& .MuiChip-label': { px: 0.5 }
                }}
              />
            )}
            {isProcessing && (
              <Chip
                label={`${progress}%`}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  bgcolor: '#fff3e0',
                  color: '#e65100',
                  '& .MuiChip-label': { px: 0.5 }
                }}
              />
            )}
          </Box>
        </Box>

        {/* Barra de progresso para processando */}
        {isProcessing && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                flex: 1,
                maxWidth: { xs: 100, sm: 150 },
                height: 4,
                borderRadius: 2,
                bgcolor: 'rgba(255, 152, 0, 0.2)',
                '& .MuiLinearProgress-bar': { bgcolor: '#ff9800' }
              }}
            />
            <Typography variant="caption" sx={{ color: '#ff9800', minWidth: 35, fontWeight: 500, display: { xs: 'none', sm: 'block' } }}>
              {progress}%
            </Typography>
            {file.estimatedSecondsRemaining && file.estimatedSecondsRemaining > 0 && (
              <Typography variant="caption" sx={{ color: '#999', fontSize: '0.7rem', display: { xs: 'none', sm: 'block' } }}>
                {formatEstimatedTime(file.estimatedSecondsRemaining)}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Data - oculta em mobile */}
      <Typography variant="body2" sx={{ color: '#666', width: 140, flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
        {formatDate(file.createdAt)}
      </Typography>

      {/* Tamanho - oculta em mobile */}
      <Typography variant="body2" sx={{ color: '#666', width: 80, flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
        {formatBytes(file.bytes)}
      </Typography>

      {/* Ações - menu em mobile, botões em desktop */}
      <Box
        className="file-actions"
        onClick={(e) => e.stopPropagation()}
        sx={{
          display: { xs: 'none', sm: 'flex' },
          opacity: 0,
          transition: 'opacity 0.2s',
          gap: 0.5,
          flexShrink: 0
        }}
      >
        <Tooltip title="Visualizar">
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onOpen(file) }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {onMoveToFolder && (
          <Tooltip title="Mover para pasta">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onMoveToFolder(file) }}
            >
              <DriveFileMoveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {onRename && (
          <Tooltip title="Renomear">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onRename(file) }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Excluir">
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onDelete(file.fileId) }}
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Menu em mobile */}
      <IconButton
        size="small"
        onClick={handleMenuOpen}
        sx={{ display: { xs: 'flex', sm: 'none' }, flexShrink: 0 }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      {/* Menu de ações (usado em mobile) */}
      <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose} onClick={(e) => e.stopPropagation()}>
        {/* Opção de seleção para mobile */}
        {onSelect && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation()
              handleAction(() => onSelect(file))
            }}
            sx={{ color: selected ? 'primary.main' : 'inherit' }}
          >
            <ListItemIcon>
              {selected ? (
                <CheckBoxIcon fontSize="small" color="primary" />
              ) : (
                <CheckBoxOutlineBlankIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText>{selected ? 'Desmarcar' : 'Selecionar'}</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={(e) => {
            e.stopPropagation()
            handleAction(() => onOpen(file))
          }}
        >
          <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Visualizar</ListItemText>
        </MenuItem>
        {onMoveToFolder && (
          <MenuItem onClick={(e) => {
            e.stopPropagation()
            handleAction(() => onMoveToFolder(file))
          }}>
            <ListItemIcon><DriveFileMoveIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Mover para pasta</ListItemText>
          </MenuItem>
        )}
        {onRename && (
          <MenuItem onClick={(e) => {
            e.stopPropagation()
            handleAction(() => onRename(file))
          }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Renomear</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={(e) => {
            e.stopPropagation()
            handleAction(() => onDelete(file.fileId))
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Excluir</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  )
}
